import Vue from "vue";
import BootstrapVue from "bootstrap-vue";
import axios from "axios";

// uncomment below for a styling easter egg
// import 'bootstrap/dist/css/bootstrap.css';

import "vselect3/dist/vue-select.css";
import "@riophae/vue-treeselect/dist/vue-treeselect.css";
import "bootstrap-vue/dist/bootstrap-vue.css";
import "vue-datetime/dist/vue-datetime.css";
import "animate.css/animate.css";
import "@sureview/v2-cameras/dist/v2-cameras.css";

import "@/utils/classComponentHooks";

import ImmixCloudAppV2 from "./App.vue";
import router from "./router";
import store from "./store/";
import { sync } from "vuex-router-sync";
import shortkey from "vue-shortkey";
import "./registerServiceWorker";
import { formatDateMixin } from "./mixins";
import Notifications from "vue-notification";
import VueSubscriptionsStore from "@/services/subscriptions.plugin";
import { applyAxiosInterceptors } from "@/services/axios.interceptors";
import VueAppConfig from "@/services/appConfig.plugin";
import Router from "vue-router";
import Camera from "@sureview/v2-cameras";
import Dispatch from "@sureview/v2-dispatch";
import { Linkify, LazyLinkifyOptions } from "@sureview/v2-linkify";
import VueSidebarMenu from "vue-sidebar-menu";
import "vue-sidebar-menu/dist/vue-sidebar-menu.css";
import api from "@/services/api.service";
import { warnLastOperator } from "@/utils/warnLastOperator";
import { handleSsoSignIn } from "@/utils/singleSignOnHelper";

Vue.use(VueSidebarMenu);

Vue.use(Router);

// On route change:
// 1. guard condition preventing unathenticated users accessing
// routes that require login.
// Such routes are marked with `meta: { auth: true }` in router definition
// 2. cancel any long-polling subscriptions.
router.beforeEach(async (to, from, next) => {
	(router.app as any).subscriptions$.unsubscribeAll();
	const authRequired = to.matched.some(route => route.meta.auth);
	const authed = store.getters.isLoggedIn;
	const closeOnLogout = to.matched.some((route: any) => route.meta.closeOnLogout);
	const redirectOnLogin = to.matched.some((route: any) => route.meta.redirectOnLogin);

	// Check with the DS to see if the user has a valid sessionID/sessionCookie.
	// If they do, this method will log them in and return a handler that takes
	// the user to their originally requested route, or the event queue.
	const handleUserAlreadyLoggedIn = async () => {
		if (!authed && (await store.dispatch("checkUserAlreadyLoggedIn"))) {
			if (to.path == "/" || to.path.indexOf("login") > -1) {
				return () => next("/event-queue");
			} else {
				return () => next();
			}
		} else if (authed) {
			if (to.path == "/" || to.path.indexOf("login") > -1) {
				return () => next("/event-queue");
			}
		}

		return false;
	};

	// Checks to see if the user is navigating to a route that requires them to
	// be authorized (and they aren't). If this is the case, this method returns
	// a handler that takes them to the login page (and includes a re-direct if
	// supported by the route).
	const handleLoggedOutUser = async () => {

		if (!authed && !to.path.includes("logout") && !from.path.includes("logout")) {
			let nextRoute = await handleSsoSignIn(window.location.href);
			if (nextRoute) {
				return () => next(nextRoute);
			}
		}

		if (authRequired && !authed) {
			if (closeOnLogout) {
				window.close();
			}
			if (redirectOnLogin) {
				return () => next("/login?redirect=" + to.fullPath);
			} else if (to.path.indexOf("login") > -1) {
				return () => next();
			} else {
				return () => next("/login");
			}
		}

		return false;
	};

	// If the user is the last operator logged in, has the last operator warning feature
	// enabled, and they're navigating away from the event queue to neither the event
	// queue or the site monitor, then this method will show them the last operator
	// warning and return a handler that will prevent them from leaving the page.
	const shouldShowLastOperatorWarning = async () => {
		if (
			from.name == "event-queue" &&
			!(to.name == "event-queue" || (to.name && to.name.includes("site-monitor"))) &&
			store.state.siteMonitor.LastOperatorMessageVisible !== false &&
			(await warnLastOperator())
		) {
			store.commit("siteMonitor/setlastOperatorMessageVisible", true);
			store.commit("siteMonitor/setRequestedRoute", to.path);
			return () => next(false);
		}

		return false;
	};

	let routeHandler =
		(await handleUserAlreadyLoggedIn()) ||
		(await handleLoggedOutUser()) ||
		(await shouldShowLastOperatorWarning()) ||
		(() => {
			store.commit("siteMonitor/setlastOperatorMessageVisible", null);
			next();
		});

	// Set the from path to the store, so we will have the route we opened the current page from
	store.dispatch("setPreviousRoute", from.fullPath);

	routeHandler();
});

// @ref https://github.com/vuejs/vuex-router-sync
const unsyncRouterStore = sync(store, router); // @techdebt on teardown

Vue.config.productionTip = false;

Vue.use(BootstrapVue);
Vue.use(Notifications);
Vue.use(shortkey, { prevent: [".prevent-shortcut"] });
Vue.mixin(formatDateMixin);
Vue.use(VueAppConfig);
Vue.use(VueSubscriptionsStore);
Vue.use(Camera);
Vue.use(Dispatch);
Vue.use(
	Linkify,
	new LazyLinkifyOptions($store => {
		// Check the dynamic link feature flag is enabled
		if ($store.getters["getFeature"](["Integration", "DynamicLink"])) {
			return $store.getters["getDynamicLinkConfig"];
		} else {
			return null;
		}
	})
);

applyAxiosInterceptors(Vue);

Vue.config.errorHandler = async function (err, vm, info) {
	if (axios.isCancel(err)) {
		return;
	}

	// Set our defaults
	let triggeringArea = "UI";
	let eventId = null;

	// Update our TriggeringArea if we know it
	if (err.stack.startsWith("[Camera.vue]")) {
		triggeringArea = "Camera Component";
	}

	// Get EventId if it exists on the vm (the error-ing Vue)
	if (
		vm &&
		vm.$store &&
		vm.$store.getters &&
		vm.$store.getters["siteMonitor/getEventDetails"] &&
		vm.$store.getters["siteMonitor/getEventDetails"].eventID
	) {
		eventId = vm.$store.getters["siteMonitor/getEventDetails"].eventID;
	}

	// If the EventId is not populated, check the store to see if its there
	if (
		eventId === null &&
		store &&
		store.getters &&
		store.getters["siteMonitor/getEventDetails"] &&
		store.getters["siteMonitor/getEventDetails"].eventID
	) {
		eventId = store.getters["siteMonitor/getEventDetails"].eventID;
	}

	// Clean out any invalid characters
	let error = ("Error: " + err.stack + ", Error Info: " + info).replace(/[^a-z0-9 ,.?!]/gi, "");

	// Raise the issue to the DataService to store
	const data = await api.addErrorToSystemLog(eventId, triggeringArea + " [" + window.location.href + "]", error);

	// Output the SystemLogID found (-1 is failure to add)
	console.log("Error added to DB. SystemLogID " + data.data);

	throw err;
};

new Vue({
	router,
	store,
	render: h => h(ImmixCloudAppV2)
}).$mount("#app");
